import emitter, { addHandler, removeHandler } from "@/mono/lib/emitter";
import { useCallback, useEffect, useRef } from "react";

const MAX_WAIT_TIME = 5000; // 5 seconds maximum wait time

export default function useEvent(
  event,
  _handler,
  callUnregisteredHandler = false,
  ...deps
) {
  const hasRunOnce = useRef(false);
  const rafId = useRef(null);
  const timeoutId = useRef(null);

  const handler = useCallback(
    (...args) => {
      if (!emitter) return;

      // Cancel any pending RAF/timeout if the event finally occurs
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
        timeoutId.current = null;
      }

      _handler(...args);
      hasRunOnce.current = true;
    },
    [emitter, ...deps]
  );

  useEffect(() => {
    if (!emitter) {
      console.warn("useEvent: missing emitter", event);
      return;
    }

    if (!event || !_handler) {
      console.warn("useEvent: missing event or handler", event, _handler);
      return;
    }

    // If we want to handle unregistered events and haven't run yet
    // TODO: This is prob not correct?
    if (callUnregisteredHandler && !hasRunOnce.current) {
      //console.log("useEvent: calling unregistered handler", event);
      let startTime = performance.now();

      const checkEvent = () => {
        // If we've exceeded our maximum wait time, stop checking
        if (performance.now() - startTime > MAX_WAIT_TIME) {
          return;
        }

        // Check if the event is in the queue
        if (emitter.all.has(event)) {
          handler();
          // Add this return to stop the RAF loop after handling
          return;
        }

        // Continue checking until we find the event or timeout
        rafId.current = requestAnimationFrame(checkEvent);
      };

      // Start checking for the event
      rafId.current = requestAnimationFrame(checkEvent);

      // Set a timeout as a safety measure
      timeoutId.current = setTimeout(() => {
        if (rafId.current) {
          cancelAnimationFrame(rafId.current);
          rafId.current = null;
        }
      }, MAX_WAIT_TIME);
    }

    addHandler(event, handler, false); // Set to false since we're handling it above

    return () => {
      removeHandler(event, handler);

      // Clean up any pending RAF/timeout
      if (rafId.current) {
        cancelAnimationFrame(rafId.current);
        rafId.current = null;
      }
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
        timeoutId.current = null;
      }
    };
  }, [emitter, event, handler, callUnregisteredHandler]);
}
